import { TopNav } from "@dashboard/components/AppLayout";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import {
  postConfigurationUpdate,
  REDACTED_SECRET_PLACEHOLDER,
  withRedactedPlaceholders,
} from "@dashboard/envSettings/services/configurationApi";
import { ExtensionsPaths } from "@dashboard/extensions/urls";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Button, Input, Spinner, Switch, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const API_BASE_URL = getAppPlatformUrl("app-configurations");

interface CaptchaLocations {
  login: boolean;
  signup: boolean;
  checkout: boolean;
  forgot_password: boolean;
}

interface CaptchaConfig {
  site_key: string;
  locations: CaptchaLocations;
  _redacted?: string[];
}

const defaultConfig = (): CaptchaConfig => ({
  site_key: "",
  locations: { login: false, signup: false, checkout: false, forgot_password: false },
});

export const CaptchaSettings: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();

  const [isActive, setIsActive] = useState(false);
  const [captchaType, setCaptchaType] = useState<"google_recaptcha" | "hcaptcha">(
    "google_recaptcha",
  );
  const [googleConfig, setGoogleConfig] = useState<CaptchaConfig>(defaultConfig());
  const [hcaptchaConfig, setHcaptchaConfig] = useState<CaptchaConfig>(defaultConfig());
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const activeConfig = captchaType === "google_recaptcha" ? googleConfig : hcaptchaConfig;
  const setActiveConfig = captchaType === "google_recaptcha" ? setGoogleConfig : setHcaptchaConfig;

  const updateConfiguration = async (
    activeStatus: boolean,
    type: "google_recaptcha" | "hcaptcha",
  ) => {
    setLoading(true);

    try {
      const tenant = getApiUrl();

      // Post both google_recaptcha and hcaptcha — only the selected type is active
      await Promise.all([
        postConfigurationUpdate({
          tenant,
          app_name: "google_recaptcha",
          configurations: {
            site_key: googleConfig.site_key,
            locations: googleConfig.locations,
            _redacted: googleConfig._redacted,
          },
          is_active: activeStatus && type === "google_recaptcha",
        }),
        postConfigurationUpdate({
          tenant,
          app_name: "hcaptcha",
          configurations: {
            site_key: hcaptchaConfig.site_key,
            locations: hcaptchaConfig.locations,
            _redacted: hcaptchaConfig._redacted,
          },
          is_active: activeStatus && type === "hcaptcha",
        }),
      ]);

      setIsActive(activeStatus);
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update configuration";

      notify({ status: "error", text: errorMessage });
      setIsActive(!activeStatus);
    } finally {
      setLoading(false);
    }
  };

  const handleMasterToggle = (value: string) => {
    const newActiveStatus = value === "active";

    updateConfiguration(newActiveStatus, captchaType);
  };

  const handleTypeChange = (value: string) => {
    setCaptchaType(value as "google_recaptcha" | "hcaptcha");
  };

  const handleSiteKeyChange = (value: string) => {
    setActiveConfig(prev => ({ ...prev, site_key: value }));
  };

  const handleLocationChange = (location: keyof CaptchaLocations, value: string) => {
    setActiveConfig(prev => ({
      ...prev,
      locations: { ...prev.locations, [location]: value === "active" },
    }));
  };

  const handleSave = () => {
    updateConfiguration(isActive, captchaType);
  };

  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const tenant = getApiUrl();

        if (!tenant) {
          setInitialLoading(false);

          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/app/get-configuration?tenant=${encodeURIComponent(tenant)}`,
          { method: "GET", headers: { "Content-Type": "application/json" } },
        );

        if (response.ok) {
          const result = await response.json();

          const googleApp = result.find((app: any) => app.app_name === "google_recaptcha");
          const hcaptchaApp = result.find((app: any) => app.app_name === "hcaptcha");

          if (googleApp?.configurations) {
            const configurations = withRedactedPlaceholders(
              googleApp.configurations,
              googleApp._redacted || googleApp.configurations._redacted || [],
            );

            setGoogleConfig({
              site_key: configurations.site_key || "",
              locations: {
                login: configurations.locations?.login ?? false,
                signup: configurations.locations?.signup ?? false,
                checkout: configurations.locations?.checkout ?? false,
                forgot_password: configurations.locations?.forgot_password ?? false,
              },
              _redacted: configurations._redacted,
            });
          }

          if (hcaptchaApp?.configurations) {
            const configurations = withRedactedPlaceholders(
              hcaptchaApp.configurations,
              hcaptchaApp._redacted || hcaptchaApp.configurations._redacted || [],
            );

            setHcaptchaConfig({
              site_key: configurations.site_key || "",
              locations: {
                login: configurations.locations?.login ?? false,
                signup: configurations.locations?.signup ?? false,
                checkout: configurations.locations?.checkout ?? false,
                forgot_password: configurations.locations?.forgot_password ?? false,
              },
              _redacted: configurations._redacted,
            });
          }

          // Master toggle on if either type is active
          if (googleApp?.is_active) {
            setIsActive(true);
            setCaptchaType("google_recaptcha");
          } else if (hcaptchaApp?.is_active) {
            setIsActive(true);
            setCaptchaType("hcaptcha");
          } else {
            setIsActive(false);
          }
        } else {
          setIsActive(false);
        }
      } catch {
        setIsActive(false);
      } finally {
        setInitialLoading(false);
      }
    };

    loadConfiguration();
  }, []);

  if (initialLoading) {
    return (
      <ListPageLayout>
        <TopNav href={ExtensionsPaths.installedExtensions} title="CAPTCHA Settings" />
        <DashboardCard paddingX={6}>
          <Box paddingY={6}>
            <Box display="flex" flexDirection="column" gap={4}>
              <Box>
                <Text size={3} color="default2">
                  Configure CAPTCHA protection for your storefront.
                </Text>
              </Box>
              <Box
                padding={4}
                borderWidth={1}
                borderColor="default1"
                borderStyle="solid"
                borderRadius={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={3}
                __minHeight="80px"
              >
                <Spinner />
                <Text size={3} color="default2">
                  Loading configuration...
                </Text>
              </Box>
            </Box>
          </Box>
        </DashboardCard>
      </ListPageLayout>
    );
  }

  return (
    <ListPageLayout>
      <TopNav href={ExtensionsPaths.installedExtensions} title="CAPTCHA Settings" />
      <DashboardCard paddingX={6}>
        <Box paddingY={6}>
          <Box display="flex" flexDirection="column" gap={4}>
            <Box>
              <Text size={3} color="default2">
                Configure CAPTCHA protection for your storefront.
              </Text>
            </Box>

            {/* Master Enable/Disable Toggle */}
            <Box
              padding={4}
              borderWidth={1}
              borderColor="default1"
              borderStyle="solid"
              borderRadius={4}
            >
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" alignItems="center" gap={3}>
                  <Text size={4} fontWeight="medium">
                    Enable CAPTCHA
                  </Text>
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Spinner />
                      <Text size={3} color="default2">
                        Updating...
                      </Text>
                    </Box>
                  ) : (
                    <Switch
                      value={isActive ? "active" : "inactive"}
                      onValueChange={handleMasterToggle}
                      disabled={loading}
                      __height="32px"
                      __width="fit-content"
                      padding={0.5}
                    >
                      <Switch.Item
                        id="captcha-inactive"
                        value="inactive"
                        name="captcha-status"
                        disabled={loading}
                        marginRight={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            Off
                          </Text>
                        </Box>
                      </Switch.Item>
                      <Switch.Item
                        id="captcha-active"
                        value="active"
                        name="captcha-status"
                        disabled={loading}
                        marginLeft={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            On
                          </Text>
                        </Box>
                      </Switch.Item>
                    </Switch>
                  )}
                </Box>
                <Text size={3} color="default2">
                  Enable CAPTCHA protection on your storefront pages.
                </Text>
              </Box>
            </Box>

            {/* Configuration — only when enabled */}
            {isActive && (
              <Box
                padding={4}
                borderWidth={1}
                borderColor="default1"
                borderStyle="solid"
                borderRadius={4}
              >
                <Box display="flex" flexDirection="column" gap={4}>
                  <Text size={4} fontWeight="medium">
                    CAPTCHA Provider
                  </Text>

                  {/* Type Selector */}
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Text size={3} fontWeight="medium">
                      Select Provider
                    </Text>
                    <Switch
                      value={captchaType}
                      onValueChange={handleTypeChange}
                      disabled={loading}
                      __height="32px"
                      __width="fit-content"
                      padding={0.5}
                    >
                      <Switch.Item
                        id="google-recaptcha"
                        value="google_recaptcha"
                        name="captcha-type"
                        disabled={loading}
                        marginRight={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            Google reCAPTCHA
                          </Text>
                        </Box>
                      </Switch.Item>
                      <Switch.Item
                        id="hcaptcha"
                        value="hcaptcha"
                        name="captcha-type"
                        disabled={loading}
                        marginLeft={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            hCAPTCHA
                          </Text>
                        </Box>
                      </Switch.Item>
                    </Switch>
                  </Box>

                  {/* Site Key */}
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Text size={3} fontWeight="medium">
                      Site Key
                    </Text>
                    <Input
                      type="text"
                      value={activeConfig.site_key}
                      onChange={e => handleSiteKeyChange(e.target.value)}
                      disabled={loading}
                      placeholder={
                        activeConfig._redacted?.includes("site_key")
                          ? REDACTED_SECRET_PLACEHOLDER
                          : captchaType === "google_recaptcha"
                            ? "Enter your Google reCAPTCHA site key"
                            : "Enter your hCAPTCHA site key"
                      }
                    />
                  </Box>

                  {/* Apply To Locations */}
                  <Box display="flex" flexDirection="column" gap={3}>
                    <Text size={3} fontWeight="medium">
                      Apply To
                    </Text>

                    {/* Login */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Text size={3}>Login Page</Text>
                      <Switch
                        value={activeConfig.locations.login ? "active" : "inactive"}
                        onValueChange={value => handleLocationChange("login", value)}
                        disabled={loading}
                        __height="28px"
                        __width="fit-content"
                        padding={0.5}
                      >
                        <Switch.Item
                          id="login-inactive"
                          value="inactive"
                          name="login-location"
                          disabled={loading}
                          marginRight={0.5}
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            paddingX={2}
                            height="100%"
                          >
                            <Text size={2} fontWeight="medium">
                              Off
                            </Text>
                          </Box>
                        </Switch.Item>
                        <Switch.Item
                          id="login-active"
                          value="active"
                          name="login-location"
                          disabled={loading}
                          marginLeft={0.5}
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            paddingX={2}
                            height="100%"
                          >
                            <Text size={2} fontWeight="medium">
                              On
                            </Text>
                          </Box>
                        </Switch.Item>
                      </Switch>
                    </Box>

                    {/* Signup */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Text size={3}>Signup Page</Text>
                      <Switch
                        value={activeConfig.locations.signup ? "active" : "inactive"}
                        onValueChange={value => handleLocationChange("signup", value)}
                        disabled={loading}
                        __height="28px"
                        __width="fit-content"
                        padding={0.5}
                      >
                        <Switch.Item
                          id="signup-inactive"
                          value="inactive"
                          name="signup-location"
                          disabled={loading}
                          marginRight={0.5}
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            paddingX={2}
                            height="100%"
                          >
                            <Text size={2} fontWeight="medium">
                              Off
                            </Text>
                          </Box>
                        </Switch.Item>
                        <Switch.Item
                          id="signup-active"
                          value="active"
                          name="signup-location"
                          disabled={loading}
                          marginLeft={0.5}
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            paddingX={2}
                            height="100%"
                          >
                            <Text size={2} fontWeight="medium">
                              On
                            </Text>
                          </Box>
                        </Switch.Item>
                      </Switch>
                    </Box>

                    {/* Checkout */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Text size={3}>Checkout Page</Text>
                      <Switch
                        value={activeConfig.locations.checkout ? "active" : "inactive"}
                        onValueChange={value => handleLocationChange("checkout", value)}
                        disabled={loading}
                        __height="28px"
                        __width="fit-content"
                        padding={0.5}
                      >
                        <Switch.Item
                          id="checkout-inactive"
                          value="inactive"
                          name="checkout-location"
                          disabled={loading}
                          marginRight={0.5}
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            paddingX={2}
                            height="100%"
                          >
                            <Text size={2} fontWeight="medium">
                              Off
                            </Text>
                          </Box>
                        </Switch.Item>
                        <Switch.Item
                          id="checkout-active"
                          value="active"
                          name="checkout-location"
                          disabled={loading}
                          marginLeft={0.5}
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            paddingX={2}
                            height="100%"
                          >
                            <Text size={2} fontWeight="medium">
                              On
                            </Text>
                          </Box>
                        </Switch.Item>
                      </Switch>
                    </Box>

                    {/* Forgot Password */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Text size={3}>Forgot Password Page</Text>
                      <Switch
                        value={activeConfig.locations.forgot_password ? "active" : "inactive"}
                        onValueChange={value => handleLocationChange("forgot_password", value)}
                        disabled={loading}
                        __height="28px"
                        __width="fit-content"
                        padding={0.5}
                      >
                        <Switch.Item
                          id="forgot-password-inactive"
                          value="inactive"
                          name="forgot-password-location"
                          disabled={loading}
                          marginRight={0.5}
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            paddingX={2}
                            height="100%"
                          >
                            <Text size={2} fontWeight="medium">
                              Off
                            </Text>
                          </Box>
                        </Switch.Item>
                        <Switch.Item
                          id="forgot-password-active"
                          value="active"
                          name="forgot-password-location"
                          disabled={loading}
                          marginLeft={0.5}
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            paddingX={2}
                            height="100%"
                          >
                            <Text size={2} fontWeight="medium">
                              On
                            </Text>
                          </Box>
                        </Switch.Item>
                      </Switch>
                    </Box>
                  </Box>

                  {/* Save Button */}
                  <Box display="flex" justifyContent="flex-end" marginTop={2}>
                    <Button variant="primary" onClick={handleSave} disabled={loading}>
                      {loading ? "Saving..." : "Save Configuration"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DashboardCard>
    </ListPageLayout>
  );
};
